import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const LS_KEY = "metros_cart_v1";
const load = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
};

// Safe UID generator — crypto.randomUUID is only available in secure contexts
// and modern browsers; fall back so adding to basket never throws.
const generateUID = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* ignore */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export function CartProvider({ children }) {
  const saved = load();
  const [items, setItems] = useState(saved.items || []);
  const [orderType, setOrderType] = useState(saved.orderType || "delivery");
  const [postcode, setPostcode] = useState(saved.postcode || "");
  const [promoCode, setPromoCode] = useState(saved.promoCode || "");
  const [note, setNote] = useState(saved.note || "");
  const [redeemPoints, setRedeemPoints] = useState(saved.redeemPoints || 0);
  const [open, setOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(saved.selectedBranch || null); // remembered for returning customers
  const [cartBranchId, setCartBranchId] = useState(saved.cartBranchId || null); // branch the current basket belongs to
  const [branchStatus, setBranchStatus] = useState(null); // live open/closed of selected branch
  const [scheduleIntent, setScheduleIntent] = useState(false); // transient: user chose "Schedule" on home
  const [totals, setTotals] = useState(null);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!selectedBranch) { setBranchStatus(null); return; }
    let active = true;
    api.get("/branch/public").then(({ data }) => {
      if (!active) return;
      const b = (data || []).find((x) => x.id === selectedBranch.id);
      setBranchStatus(b ? { is_open: b.is_open, opens_at: b.opens_at, closes_at: b.closes_at } : null);
    }).catch(() => {});
    return () => { active = false; };
  }, [selectedBranch]);
  const debounce = useRef(null);

  useEffect(() => {
    // Strip heavy inline images (data URLs) before persisting so a product with a
    // large uploaded image can never blow the localStorage quota and crash the app.
    const light = items.map((i) => {
      const img = typeof i.image === "string" && (i.image.startsWith("data:") || i.image.length > 300) ? "" : i.image;
      return { ...i, image: img };
    });
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ items: light, orderType, postcode, promoCode, note, redeemPoints, selectedBranch, cartBranchId }));
    } catch { /* quota exceeded or unavailable — safe to ignore */ }
  }, [items, orderType, postcode, promoCode, note, redeemPoints, selectedBranch, cartBranchId]);

  // Products are branch-specific: if the customer switches branch, clear the basket
  // (items from another branch don't exist on the new branch's menu) and prompt to re-add.
  useEffect(() => {
    const bid = selectedBranch?.id;
    if (!bid) return;
    if (cartBranchId && bid !== cartBranchId && items.length > 0) {
      setItems([]); setPromoCode(""); setNote(""); setRedeemPoints(0);
      setCartBranchId(bid);
      toast.info(`Your basket was cleared because you switched to ${selectedBranch.name || "another branch"}. Menus differ by branch — please add items again.`);
    } else if (bid !== cartBranchId) {
      setCartBranchId(bid);
    }
  }, [selectedBranch, cartBranchId, items.length]);

  const itemCount = items.reduce((n, i) => n + i.quantity, 0);

  // Re-hydrate item images on load: images are stripped from localStorage to protect the
  // storage quota (large base64 uploads), so after a reload a basket item can have no image.
  // Fetch each product once and restore its image so the basket/checkout thumbnails show.
  useEffect(() => {
    const ids = [...new Set(items.filter((i) => i.product_id && !i.image).map((i) => i.product_id))];
    if (ids.length === 0) return;
    let active = true;
    (async () => {
      const results = await Promise.all(ids.map((id) => api.get(`/menu/products/${id}`).then(({ data }) => data).catch(() => null)));
      if (!active) return;
      const imgById = {};
      results.forEach((p) => { if (p && p.image) imgById[p.id] = p.image; });
      if (Object.keys(imgById).length) {
        setItems((prev) => prev.map((i) => (!i.image && imgById[i.product_id]) ? { ...i, image: imgById[i.product_id] } : i));
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = useCallback((item) => {
    setItems((prev) => [...prev, { ...item, uid: generateUID() }]);
    setCartBranchId((prev) => prev || selectedBranch?.id || null);
  }, [selectedBranch]);

  const updateQty = useCallback((uid, delta) => {
    setItems((prev) => {
      const target = prev.find((i) => i.uid === uid);
      if (!target) return prev;
      const newQty = Math.max(1, target.quantity + delta);
      if (target.meal_group) {
        return prev.map((i) => (i.meal_group === target.meal_group ? { ...i, quantity: newQty } : i));
      }
      return prev.map((i) => (i.uid === uid ? { ...i, quantity: newQty } : i));
    });
  }, []);

  const removeItem = useCallback((uid) => {
    setItems((prev) => {
      const target = prev.find((i) => i.uid === uid);
      if (target?.meal_group) return prev.filter((i) => i.meal_group !== target.meal_group);
      return prev.filter((i) => i.uid !== uid);
    });
  }, []);

  const clearCart = useCallback(() => { setItems([]); setPromoCode(""); setNote(""); setRedeemPoints(0); setCartBranchId(null); }, []);

  const payload = useCallback(() => ({
    type: orderType,
    items: items.map((i) => ({
      product_id: i.product_id, quantity: i.quantity,
      selected_options: (i.selected_options || []).map((o) => o.id),
      meal_addons: (i.meal_addons || []).map((a) => a.id),
      special_instructions: i.special_instructions || "",
      offer_id: i.offer_id || null,
      meal_group: i.meal_group || null,
      reward_item_id: i.reward_item_id || null,
    })),
    promo_code: promoCode,
    postcode,
    redeem_points: redeemPoints,
    branch_id: selectedBranch?.id || null,
  }), [items, orderType, promoCode, postcode, redeemPoints, selectedBranch]);

  useEffect(() => {
    if (items.length === 0) { setTotals(null); setValidationError(""); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/orders/validate", payload());
        setTotals(data);
        setValidationError("");
      } catch (e) {
        setValidationError(apiError(e));
        setTotals(null);
      }
    }, 350);
    return () => clearTimeout(debounce.current);
  }, [items, orderType, promoCode, postcode, redeemPoints, payload]);

  return (
    <CartContext.Provider value={{
      items, itemCount, addItem, updateQty, removeItem, clearCart,
      orderType, setOrderType, postcode, setPostcode, promoCode, setPromoCode,
      note, setNote, redeemPoints, setRedeemPoints, open, setOpen, totals, validationError, payload,
      selectedBranch, setSelectedBranch, branchStatus,
      scheduleIntent, setScheduleIntent,
    }}>
      {children}
    </CartContext.Provider>
  );
}
